import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Heading,
  Skeleton,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  CompanyDocumentType,
  CompanyDraftStatus,
  DocumentStatus,
  DraftRepresentative,
  RepresentativeRole,
  SpvDraftDocumentInfosFragment,
  useGetSpvDraftQuery,
  usePreviewSpvDraftDocumentLazyQuery,
  useValidateSpvDraftDocumentMutation,
} from '@blockpulse3/graphql/hooks';
import { b64toBlob } from '@blockpulse3/helpers';
import {
  ErrorQueryCard,
  CONTENT_ERROR as PDF_URL_ERROR,
  CONTENT_LOADING as PDF_URL_LOADING,
  SignDocumentModal,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { SPVMatriculationDeposit } from './SPVMatriculationDeposit';
import {
  SPVMatriculationDocuments,
  SelectableCompanyDocumentType,
} from './SPVMatriculationDocuments';
import { SPVMatriculationIdentity } from './SPVMatriculationIdentity';

type Props = unknown;

export function SPVMatriculation(): JSX.Element {
  const i18nBadge = useTranslations('Badge');
  const i18nDocumentValues = useTranslations('DocumentValues');
  const i18nSPVMatriculation = useTranslations('SPVMatriculation');

  const { user } = useUser();

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const [selectedDocument, setSelectedDocument] = useState<SelectableCompanyDocumentType | null>(
    null,
  );
  const [documentPreviews, setDocPrev] = useState<
    Record<SelectableCompanyDocumentType, string | symbol>
  >({
    DBE: PDF_URL_LOADING,
    DNC: PDF_URL_LOADING,
    DOMICILIATION: PDF_URL_LOADING,
    POUVOIR_FORMALITES: PDF_URL_LOADING,
    SOUSCRIPTEURS: PDF_URL_LOADING,
    STATUTS: PDF_URL_LOADING,
    FORMULAIRE_M0: PDF_URL_LOADING,
  });

  /* ** Controls modal open status ** */
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { id = '' } = useParams();
  const { data, loading, error, refetch } = useGetSpvDraftQuery({ variables: { id } });

  const [loadPreview] = usePreviewSpvDraftDocumentLazyQuery();

  /* ** Dynamic modal title ** */
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [documentType, setDocumentType] = useState<CompanyDocumentType | null>(null);
  const [documentStatus, setDocumentStatus] = useState<DocumentStatus | null>(null);
  const [validateSpvDraftDocumentMutation, { loading: isDocumentValidating }] =
    useValidateSpvDraftDocumentMutation();

  if (loading) {
    return (
      <Skeleton>
        <Card h="380px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="380px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="380px" />;
  }

  const draft = data.spvDraft;
  const url = selectedDocument ? documentPreviews[selectedDocument] : PDF_URL_LOADING;

  /* ** Is current user a signer and able to be redirected to SignatureIt ** */
  const president: DraftRepresentative = draft.content.representatives.find(
    (rep: DraftRepresentative) =>
      rep.role === RepresentativeRole.PRESIDENT && rep.email === user?.email,
  );

  const handleDocumentSign = (): void => {
    if (!documentType) return;
    validateSpvDraftDocumentMutation({
      variables: {
        validateSPVDraftDocumentInput: {
          companyDraftId: id,
          documentType,
        },
      },
      onCompleted: (data) => {
        if (data.validateSPVDraftDocument?.signingUrl) {
          window.open(data.validateSPVDraftDocument.signingUrl, '_self');
        } else {
          successToast({ title: i18nSPVMatriculation('Toast.SignatureSentSuccess') });
          setIsDisabled(true);
        }
        /* ** Update lastRemindEmailSentAt ** */
        refetch();
        setDocumentStatus(DocumentStatus.ONGOING);
      },
      onError: () => {
        errorToast({ title: i18nSPVMatriculation('Toast.SignatureSentError') });
      },
    });
  };

  const handlePreviewModalClose = (): void => {
    onClose();
  };

  const loadPreviewURL = (type: CompanyDocumentType): void => {
    loadPreview({
      variables: {
        id,
        documentType: type,
      },
      onCompleted: ({ pdfURL }: { pdfURL: string }) => {
        let objURL: string;
        if (pdfURL.startsWith('http')) {
          objURL = pdfURL;
        } else {
          const blob = b64toBlob(pdfURL, 'application/pdf');
          objURL = URL.createObjectURL(blob);
        }
        setDocPrev((curr) => ({ ...curr, [type]: objURL }));
      },
      onError: () => {
        setDocPrev((curr) => ({ ...curr, [type]: PDF_URL_ERROR }));
      },
    });
  };

  const handleDocumentClick = (
    type: SelectableCompanyDocumentType,
    doc: SpvDraftDocumentInfosFragment,
  ): void => {
    const title = ('Document' + type) as keyof IntlMessages['DocumentValues'];

    setDocumentType(type);
    setSelectedDocument(type);
    setModalTitle(i18nDocumentValues(title));

    if (doc) {
      const { lastRemindEmailSentAt, createdAt } = doc.document.signature || {};
      const shouldRemind = dayjs(lastRemindEmailSentAt || createdAt).isBefore(
        dayjs().subtract(1, 'h'),
      );
      setIsDisabled(!shouldRemind);
      setDocumentStatus(doc.document.status);
    } else {
      setIsDisabled(false);
      setDocumentStatus(null);
    }

    onOpen();

    if (typeof documentPreviews[type] !== 'string') {
      loadPreviewURL(type);
    }
  };

  return (
    <Card>
      <CardHeader>
        <HStack>
          <Heading size="md">{i18nSPVMatriculation('Title')}</Heading>
          <Badge colorScheme="yellow">{i18nBadge('InProgress')}</Badge>
        </HStack>
      </CardHeader>
      <CardBody>
        <Stack spacing="3">
          {draft?.status === CompanyDraftStatus.EDITING && (
            <Alert status="warning">
              <AlertIcon as={ExclamationIcon} />
              <Stack spacing="1">
                <AlertTitle>{i18nSPVMatriculation('AlertEditingTitle')}</AlertTitle>
                <AlertDescription>{i18nSPVMatriculation('AlertEditingSubTitle')}</AlertDescription>
              </Stack>
            </Alert>
          )}
          <SPVMatriculationDocuments onDocumentClick={handleDocumentClick} />
          <SPVMatriculationIdentity />
          <SPVMatriculationDeposit />
        </Stack>
      </CardBody>
      <SignDocumentModal
        isDisabled={isDisabled}
        isOpen={isOpen}
        isSignatureLoading={isDocumentValidating}
        isUserSigner={!!president}
        src={url}
        status={documentStatus}
        title={modalTitle}
        onCancel={handlePreviewModalClose}
        onClose={handlePreviewModalClose}
        onSign={handleDocumentSign}
      />
    </Card>
  );
}

export type SPVMatriculationProps = Props;
